import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow: hidden;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 0.31250625rem;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 0.31250625rem;
                          `
                        : css`
                              margin-top: 0.250625rem;
                          `}
            }
            cursor: pointer;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 0.31250625rem;
                          `
                        : css`
                              margin-top: 0.250625rem;
                          `}
            }
            cursor: pointer;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 0.31250625rem;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 0.31250625rem;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const extractNumericValue = (input: any) => {
        const numericPart = input.replace(/[^\d.]/g, "");
        return parseFloat(numericPart) || 0;
    };

    const sortedFacets = [...facets].sort((a, b) => {
        const numericA = extractNumericValue(a.name);
        const numericB = extractNumericValue(b.name);
        return numericA - numericB;
    });

    const limitedFacets = expanded ? sortedFacets : sortedFacets?.slice(0, showMoreLimit);
    const anySelected = sortedFacets.find(f => f.selected) !== undefined;

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            <Hidden below="lg">
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup} aria-labelledby={`${facet.id}-label`}>
                        <VisuallyHidden as="label" id={`${facet.id}-label`}>
                            {facet.name}
                        </VisuallyHidden>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See Less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See All")}
                        </Link>
                    ))}
            </Hidden>
            <Hidden above="md">
                {facets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup} aria-labelledby={`${facet.id}-label`}>
                        <VisuallyHidden as="label" id={`${facet.id}-label`}>
                            {facet.name}
                        </VisuallyHidden>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            </Hidden>
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
